import React, { FC } from 'react';
import { HiCheckCircle } from 'react-icons/hi';
import { Link } from 'gatsby';

import { useUiComponentsInternationalContext } from '@repo/static-translations/src';
import { openMavenoidChat } from '@repo/ui-components/openChatWidget';
import { ConfirmationProps } from './BlockBuilderProspectScheduler.types';
import { removeLeadingZeroFromTime } from './utils';

const COMPANY_PHONE_NUMBER = '18005783647';

const PhoneLink: FC<{ number: string }> = ({ number }) => (
  <a href={`tel:+${number}`}>{`${number.slice(0, 1)} (${number.slice(
    1,
    4
  )}) ${number.slice(4, 7)}-${number.slice(7)}`}</a>
);

const DealerPhoneLink: FC<{ number: string }> = ({ number }) => (
  <a href={`tel:${number}`}>{`(${number.slice(0, 3)}) ${number.slice(
    3,
    6
  )}-${number.slice(6)}`}</a>
);

interface ChatButtonProps {
  tt: ReturnType<typeof useUiComponentsInternationalContext>['tt'];
}

const ChatButton = ({ tt }: ChatButtonProps) => (
  <button
    type="button"
    onClick={openMavenoidChat}
    className="text-foreground-link underline hover:no-underline"
    aria-label={tt(s => s.blockBuilderProspectSchedulerComponent.startChatText)}
  >
    {tt(s => s.blockBuilderProspectSchedulerComponent.startChatText)}
  </button>
);

interface QuestionsBlockProps {
  dealer: {
    isCod?: boolean;
    name?: string;
    phone?: string;
  };
  tt: ReturnType<typeof useUiComponentsInternationalContext>['tt'];
}

const QuestionsBlock: FC<QuestionsBlockProps> = ({ dealer, tt }) => {
  if (dealer.isCod) {
    return (
      <p>
        {tt(
          s =>
            s.blockBuilderProspectSchedulerComponent
              .questionsBlockMessageCompanyOwnedDealer,
          {
            phoneNumber: <PhoneLink number={COMPANY_PHONE_NUMBER} />,
            chatButton: <ChatButton tt={tt} />,
          }
        )}
      </p>
    );
  }
  return (
    <p>
      {tt(
        s =>
          s.blockBuilderProspectSchedulerComponent
            .questionsBlockMessageNonCompanyOwnedDealer,
        {
          dealerName: dealer.name,
          phoneNumber: <PhoneLink number={dealer.phone} />,
        }
      )}
    </p>
  );
};

const EstimatedAppointmentBlock: FC<{
  estimatedAppointmentLength: string;
  message: string;
}> = ({ estimatedAppointmentLength, message }) => (
  <p>
    {message}
    :
    <br />
    <strong>{estimatedAppointmentLength}</strong>
  </p>
);

const Confirmation: React.FC<Readonly<ConfirmationProps>> = ({
  confirmedAppointmentData,
  confirmationScenario,
  dealer,
}) => {
  const { tt } = useUiComponentsInternationalContext();

  if (!confirmationScenario) return null;

  switch (confirmationScenario) {
    case 'existingContact':
      return (
        <div>
          <h2 className="flex items-center gap-2 mb-4">
            {tt(
              s => s.blockBuilderProspectSchedulerComponent.welcomeBackMessage
            )}
          </h2>
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent
                  .alreadyHaveAccountHeader
            )}
          </p>
          <p>
            <strong>
              {tt(
                s =>
                  s.blockBuilderProspectSchedulerComponent
                    .needToScheduleAppointmentMessage
              )}
            </strong>{' '}
            <br />
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent.logInToAccountMessage,
              {
                accountLink: <Link to="/account/appointments">account</Link>,
                phoneLink: <PhoneLink number={COMPANY_PHONE_NUMBER} />,
              }
            )}
          </p>
          <p>
            <strong>
              {tt(
                s =>
                  s.blockBuilderProspectSchedulerComponent
                    .wannaUpgradeSystemQuestion
              )}
            </strong>{' '}
            <br />
          </p>
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent
                  .weLookForwardToAssistingYouMessage
            )}
          </p>
        </div>
      );

    case 'noDealersAvailable':
      return (
        <div>
          <h2 className="flex items-center gap-2 mb-4">
            {tt(
              s => s.blockBuilderProspectSchedulerComponent.noDealersErrorTitle
            )}
          </h2>
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent.noDealersErrorMessage
            )}
          </p>
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent
                  .questionsBlockMessageCompanyOwnedDealer,
              {
                phoneNumber: <PhoneLink number={COMPANY_PHONE_NUMBER} />,
                chatButton: <ChatButton tt={tt} />,
              }
            )}
          </p>
        </div>
      );

    case 'noAppointmentsAvailable':
    case 'isEmailOrCrmDealer':
      return (
        <div>
          <h2 className="flex items-center gap-2 mb-4">
            <HiCheckCircle color="green" />{' '}
            {tt(s => s.blockBuilderProspectSchedulerComponent.thankYouHeader)}
          </h2>
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent.dealerNotifiedMessage,
              { dealerName: <strong>{dealer.name}</strong> }
            )}
          </p>
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent
                  .immediateAssistanceMessage,
              {
                dealer: dealer.name,
                phone: <DealerPhoneLink number={dealer.phone} />,
              }
            )}
          </p>
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent
                  .thankYouForChoosingUsMessage
            )}
          </p>
        </div>
      );

    case 'appointmentConfirmedVirtual':
      return (
        <div>
          <h2 className="flex items-center gap-2 mb-4">
            <HiCheckCircle color="green" />{' '}
            {tt(
              s =>
                s.blockBuilderReferralFormComponent
                  .youAreAllSetSuccessMessageHeader
            )}
          </h2>
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent
                  .confirmedVirtualConsultation,
              {
                dealer: <strong>{dealer.name}</strong>,
              }
            )}{' '}
            <br />
            <strong>{confirmedAppointmentData.slotDateFormatted}</strong>,{' '}
            <strong>
              {removeLeadingZeroFromTime(confirmedAppointmentData.slotDisplay)}.
            </strong>
          </p>
          {confirmedAppointmentData.estimatedAppointmentLength && (
            <EstimatedAppointmentBlock
              estimatedAppointmentLength={
                confirmedAppointmentData.estimatedAppointmentLength
              }
              message={tt(
                s =>
                  s.blockBuilderProspectSchedulerComponent
                    .estimatedAppointmentMessage
              )}
            />
          )}
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent.additionalEmailMessage
            )}
          </p>
          <QuestionsBlock dealer={dealer} tt={tt} />
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent
                  .thankYouForChoosingUsMessage
            )}
          </p>
        </div>
      );

    case 'appointmentConfirmedInHome':
      return (
        <div>
          <h2 className="flex items-center gap-2 mb-4">
            <HiCheckCircle color="green" />{' '}
            {tt(
              s =>
                s.blockBuilderReferralFormComponent
                  .youAreAllSetSuccessMessageHeader
            )}
          </h2>
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent
                  .confirmedInHomeConsultation,
              {
                dealer: <strong>{dealer.name}</strong>,
              }
            )}{' '}
            <br />
            <strong>{confirmedAppointmentData.slotDateFormatted}</strong>,{' '}
            <strong>
              {removeLeadingZeroFromTime(confirmedAppointmentData.slotDisplay)}.
            </strong>
          </p>
          {confirmedAppointmentData.estimatedAppointmentLength && (
            <EstimatedAppointmentBlock
              estimatedAppointmentLength={
                confirmedAppointmentData.estimatedAppointmentLength
              }
              message={tt(
                s =>
                  s.blockBuilderProspectSchedulerComponent
                    .estimatedAppointmentMessage
              )}
            />
          )}
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent.additionalEmailMessage
            )}
          </p>
          <QuestionsBlock dealer={dealer} tt={tt} />
          <p>
            {tt(
              s =>
                s.blockBuilderProspectSchedulerComponent
                  .thankYouForChoosingUsMessage
            )}
          </p>
        </div>
      );

    default:
      return null;
  }
};

export default Confirmation;
